import { Command as CmdkCommand } from "cmdk";
import { useEffect } from "react";
import { useCommandRegistry, type Command } from "@praesto/core";
import { useCore } from "../lib/core";
import { toggleSubSidebar } from "../lib/sidebar-state";

/**
 * Cmd+K / Ctrl+K overlay. RBAC-filtered commands grouped by category.
 * cmdk handles fuzzy search + keyboard navigation. Esc to close.
 */
export function CommandPalette() {
  const { commands, open, setOpen } = useCommandRegistry();
  const core = useCore();

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const isMacCmd = e.metaKey && e.key === "k";
      const isWinCtrl = e.ctrlKey && e.key === "k";
      const isMacSidebar = e.metaKey && e.key === "\\";
      const isWinSidebar = e.ctrlKey && e.key === "\\";
      if (isMacCmd || isWinCtrl) {
        e.preventDefault();
        setOpen(!open);
        return;
      }
      if (isMacSidebar || isWinSidebar) {
        e.preventDefault();
        toggleSubSidebar();
        return;
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, setOpen]);

  if (!open) return null;

  const allowed = commands.filter(
    (c) => !c.requires || core.rbac.can(c.requires.action, c.requires.resource),
  );

  const byCategory = allowed.reduce<Record<string, Command[]>>((acc, cmd) => {
    const cat = cmd.category ?? "action";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(cmd);
    return acc;
  }, {});

  function run(cmd: Command): void {
    setOpen(false);
    void cmd.action();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-4 sm:pt-24 px-4">
      <div
        className="absolute inset-0 bg-overlay"
        onClick={() => setOpen(false)}
        aria-label={core.i18n.t("shell.action.close")}
      />
      <CmdkCommand
        className="relative w-full max-w-lg bg-elevated border border-border-default rounded-lg shadow-lg overflow-hidden"
        label="Palette comandi"
      >
        <CmdkCommand.Input
          autoFocus
          placeholder={core.i18n.t("shell.command.placeholder")}
          className="w-full px-4 py-3 bg-transparent text-base sm:text-sm text-fg-default outline-none border-b border-border-default font-sans"
        />
        <CmdkCommand.List className="max-h-[60vh] sm:max-h-[400px] overflow-auto py-1">
          <CmdkCommand.Empty className="px-4 py-6 text-center text-fg-muted text-sm">
            {core.i18n.t("shell.command.empty")}
          </CmdkCommand.Empty>
          {Object.entries(byCategory).map(([cat, cmds]) => (
            <CmdkCommand.Group key={cat} heading={categoryLabel(cat, core)} className="px-1 py-1">
              {cmds.map((c) => {
                const Icon = c.icon;
                return (
                  <CmdkCommand.Item
                    key={c.id}
                    value={`${c.label} ${c.id}`}
                    onSelect={() => run(c)}
                    className="min-h-touch px-3 py-2 rounded-sm text-base sm:text-sm flex items-center gap-2 cursor-pointer aria-selected:bg-active text-fg-default"
                  >
                    {Icon && <Icon size={14} />}
                    <span className="flex-1">{c.label}</span>
                    {c.shortcut && (
                      // Static text never sits on an interaction veil (bg-active):
                      // bg-subtle is the opaque quiet surface for kbd hints.
                      <kbd className="text-mono-xs px-1.5 py-0.5 bg-subtle rounded-sm text-fg-muted">
                        {c.shortcut}
                      </kbd>
                    )}
                  </CmdkCommand.Item>
                );
              })}
            </CmdkCommand.Group>
          ))}
        </CmdkCommand.List>
        <div className="px-3 py-2 border-t border-border-default text-mono-xs text-fg-muted hidden sm:flex justify-between">
          <span>{core.i18n.t("shell.command.hint.run")}</span>
          <span>{core.i18n.t("shell.command.hint.shortcuts")}</span>
        </div>
      </CmdkCommand>
    </div>
  );
}

function categoryLabel(cat: string, core: ReturnType<typeof useCore>): string {
  switch (cat) {
    case "navigation":
      return core.i18n.t("shell.command.category.navigation");
    case "action":
      return core.i18n.t("shell.command.category.action");
    case "search":
      return core.i18n.t("shell.command.category.search");
    default:
      return cat;
  }
}
